import { useCallback, useEffect } from 'react';

import { useVideo } from 'lib/hooks/queries/video/useVideo';
import { useVideoProgress } from 'lib/hooks/queries/video/useVideoProgress';

import Button from 'components/shared/Button/Button';
import Skeleton from 'components/shared/Skeleton/Skeleton';
import VideoPlayer from 'components/shared/VideoPlayer/VideoPlayer';
import { COMPLETED_STATUSES } from 'components/shared/Stepper/lib/constants';

import useTrainingVideoPlayback from './lib/useTrainingVideoPlayback';

// Video lookup codes that mean "no video published yet" (not found / inactive) —
// any other non-zero code is a real failure and must not render as coming-soon
const NO_VIDEO_CODES = [6, 7];

const TrainingVideo = ({
  isAdmin,
  setHeaderButtonProps,
  addFooterActions,
  onNext,
  orchestratorData,
  mutateOrchestratorData,
  orchestratorLoading
}) => {
  // CONVENTION: an onboarding training video's file_key is always
  // `myriad-videos/<step code>`, so the step code itself is the lookup —
  // see backend utility/wizard/config_registry/onboarding/video_registry.js
  const stepCode = orchestratorData?.stepCode;
  const {
    data: videoResponse,
    isLoading: videoLoading,
    isError: videoQueryFailed,
    refetch: refetchVideo
  } = useVideo({
    params: { file_name: stepCode },
    options: { enabled: !!stepCode }
  });
  const video = videoResponse?.code === 0 ? videoResponse?.data : null;

  // Progress is volatile: playback heartbeats write to the server without touching
  // this cache, so refetch on every visit to the step — resume position and
  // completion state must be current
  const { data: progressResponse, isFetching: progressFetching } = useVideoProgress({
    params: { video_id: video?.id },
    options: {
      enabled: !isAdmin && !!video?.id,
      refetchOnMount: 'always',
      staleTime: 0
    }
  });
  const savedProgress = progressResponse?.code === 0 ? progressResponse?.data : null;

  const { playerRef, hasEnded, trackProgress } = useTrainingVideoPlayback({
    isAdmin,
    video,
    orchestratorData,
    savedProgress,
    progressFetching
  });

  // A failed lookup is NOT a missing video: show a retry state and keep Continue
  // locked instead of letting a backend/S3 misconfiguration render as "coming soon"
  const videoErrored =
    videoQueryFailed ||
    (!!videoResponse && videoResponse.code !== 0 && !NO_VIDEO_CODES.includes(videoResponse.code));
  const videoUnavailable = !stepCode || (!videoLoading && !videoErrored && !video?.presignedUrl);
  const isSubstepCompleted = COMPLETED_STATUSES.includes(orchestratorData?.handlerStatus);
  const hasWatchedVideo = hasEnded || !!savedProgress?.is_completed;
  const canContinue = isAdmin || isSubstepCompleted || hasWatchedVideo || videoUnavailable;

  const handleContinue = useCallback(() => {
    if (isAdmin || !video?.id) {
      onNext();
      return;
    }
    mutateOrchestratorData({ video_id: video.id });
  }, [isAdmin, video?.id, onNext, mutateOrchestratorData]);

  useEffect(() => {
    setHeaderButtonProps(null);
    addFooterActions([
      {
        text: 'Continue',
        onClick: handleContinue,
        isDisabled: !canContinue || orchestratorLoading
      }
    ]);
  }, [canContinue, orchestratorLoading, handleContinue]);

  return (
    <div className="w-full overflow-hidden rounded-lg">
      {stepCode && videoLoading ? (
        <Skeleton height="400px" />
      ) : videoErrored ? (
        <div className="flex h-[400px] w-full flex-col items-center justify-center rounded-lg bg-neutral-50 text-center">
          <p className="text-base font-medium text-neutral-800">
            We couldn&apos;t load the training video
          </p>
          <p className="pb-3 pt-1 text-sm text-neutral-600">
            Something went wrong on our end or with your connection.
          </p>
          <Button
            text="Try again"
            color="primary"
            id="trainingVideoRetryBtn"
            onClick={() => refetchVideo()}
          />
        </div>
      ) : video?.presignedUrl ? (
        <div className="flex w-full justify-center">
          <div className="w-full max-w-[711px]">
            <VideoPlayer ref={playerRef} src={video.presignedUrl} onAnalytics={trackProgress} />
          </div>
        </div>
      ) : (
        <div className="flex h-[400px] w-full flex-col items-center justify-center rounded-lg bg-neutral-50 text-center">
          <p className="text-base font-medium text-neutral-800">Training video coming soon</p>
          <p className="pt-1 text-sm text-neutral-600">
            This training video isn&apos;t available yet. You can continue and come back to watch it
            later.
          </p>
        </div>
      )}
    </div>
  );
};

export default TrainingVideo;
